import { NavLink, Navigate, Route, Routes, useLocation, useSearchParams } from "react-router-dom";
import {
  ClipboardCheck,
  Gauge,
  LogOut,
  Settings,
  TriangleAlert,
  Truck,
  UserRound,
  Wrench
} from "lucide-react";
import { useAuth } from "./auth";
import { AdminPage } from "./pages/AdminPage";
import { AuthPage } from "./pages/AuthPage";
import { DashboardPage } from "./pages/DashboardPage";
import { InspectionsPage } from "./pages/InspectionsPage";
import { InspectionReportPage } from "./pages/InspectionReportPage";
import { MaintenancePage } from "./pages/MaintenancePage";
import { NCsPage } from "./pages/NCsPage";
import { NCReportPage } from "./pages/NCReportPage";
import { ProfilePage } from "./pages/ProfilePage";
import { VehiclesPage } from "./pages/VehiclesPage";

const mainLinks = [
  { to: "/", label: "Painel", icon: Gauge },
  { to: "/veiculos", label: "Veículos", icon: Truck },
  { to: "/inspecoes", label: "Inspeções", icon: ClipboardCheck },
  { to: "/nao-conformidades", label: "NCs", icon: TriangleAlert },
  { to: "/manutencao", label: "Manutenção", icon: Wrench }
];

function Shell() {
  const { user, logout } = useAuth();
  const location = useLocation();
  if (!user) {
    const next = location.pathname + location.search + location.hash;
    return <Navigate to={"/entrar?next=" + encodeURIComponent(next)} replace />;
  }

  const initial = user.name?.trim().charAt(0).toUpperCase() || "U";

  return (
    <div className="app-shell">
      <aside className="sidebar">
        <div className="sidebar-brand">
          <img src="/mm-logo.svg" alt="MM Locações" className="brand-logo" />
          <div className="product-identity">
            <strong>MM Fleet</strong>
            <span>Gestão e Inspeção de Frota</span>
          </div>
        </div>

        <nav className="sidebar-nav">
          <p className="nav-caption">GESTÃO DA FROTA</p>
          {mainLinks.map(({ to, label, icon: Icon }) => (
            <NavLink key={to} to={to} end={to === "/"}>
              <Icon size={19} strokeWidth={2.2} />
              <span>{label}</span>
            </NavLink>
          ))}

          <p className="nav-caption nav-caption-space">CONTA E SISTEMA</p>
          {user.role === "admin" && (
            <NavLink to="/admin">
              <Settings size={19} strokeWidth={2.2} />
              <span>Administração</span>
            </NavLink>
          )}
          <NavLink to="/perfil">
            <UserRound size={19} strokeWidth={2.2} />
            <span>Meu perfil</span>
          </NavLink>
        </nav>

        <div className="sidebar-user">
          <div className="user-avatar">{initial}</div>
          <div className="user-copy">
            <strong>{user.name}</strong>
            <small>{user.role === "admin" ? "Administrador" : "Inspetor"}</small>
          </div>
          <button className="logout-icon" onClick={logout} title="Sair" aria-label="Sair">
            <LogOut size={18} />
          </button>
        </div>
      </aside>

      <div className="app-main">
        <header className="mobile-topbar">
          <img src="/mm-logo.svg" alt="MM Locações" />
          <div className="mobile-topbar-actions">
            {user.role === "admin" && (
              <NavLink to="/admin" className="mobile-admin-shortcut" aria-label="Administração">
                <Settings size={18} />
                <span>Admin</span>
              </NavLink>
            )}
            <NavLink to="/perfil" className="mobile-avatar" aria-label="Meu perfil">{initial}</NavLink>
          </div>
        </header>

        <main className="content">
          <Routes>
            <Route path="/" element={<DashboardPage />} />
            <Route path="/veiculos" element={<VehiclesPage />} />
            <Route path="/inspecoes" element={<InspectionsPage />} />
            <Route path="/inspecoes/nova/:vehicleId" element={<InspectionsPage />} />
            <Route path="/inspecoes/:id/relatorio" element={<InspectionReportPage />} />
            <Route path="/nao-conformidades" element={<NCsPage />} />
            <Route path="/nao-conformidades/relatorio" element={<NCReportPage />} />
            <Route path="/manutencao" element={<MaintenancePage />} />
            <Route path="/perfil" element={<ProfilePage />} />
            <Route path="/admin" element={user.role === "admin" ? <AdminPage /> : <Navigate to="/" replace />} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </main>

        <nav className="mobile-nav">
          {mainLinks.map(({ to, label, icon: Icon }) => (
            <NavLink key={to} to={to} end={to === "/"}>
              <Icon size={20} strokeWidth={2.2} />
              <span>{label}</span>
            </NavLink>
          ))}
          <NavLink to="/perfil">
            <UserRound size={20} strokeWidth={2.2} />
            <span>Perfil</span>
          </NavLink>
        </nav>
      </div>
    </div>
  );
}

function LoginRoute() {
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const requested = searchParams.get("next") || "/";
  const next = requested.startsWith("/") && !requested.startsWith("//") ? requested : "/";
  return user ? <Navigate to={next} replace /> : <AuthPage />;
}

export default function App() {
  const { user, loading } = useAuth();
  if (loading) return <div className="center-page">Carregando MM Fleet...</div>;

  return (
    <Routes>
      <Route path="/entrar" element={<LoginRoute />} />
      <Route path="/*" element={<Shell />} />
    </Routes>
  );
}
